import { getIncomers, getOutgoers } from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';
import {
  AIAgentType,
  AIChannelType,
  TransferToEscalationOption,
  type LBNodeData,
} from '@/proto/logiblocks/logiblocks';
import {
  getLBNodeInfo,
  getLBNodeType,
  getNodeInfoForType,
  isProductionReadyNodeType,
  NodeType,
} from './nodes/registry';
import {
  LBNodeDataWithIndexer,
  NodeCategory,
  OutputType,
  type ReactFlowEdge,
  type ReactFlowNode,
} from './types';
import { LBEDGE_MARKER_END } from './styles';

/**
 * Gets all ancestor nodes of a given node (nodes that are upstream in the graph).
 *
 * Traverses both:
 * - Edge-based ancestors (nodes with edges pointing to this node)
 * - Subgraph parent chain (via parentId) - if a node is inside a subgraph,
 *   the subgraph parent and its ancestors are included
 *
 * @param nodeId - The ID of the node to find ancestors for
 * @param nodes - All nodes in the graph
 * @param edges - All edges in the graph
 * @returns Array of ancestor nodes in BFS order (closest ancestors first), excludes the starting node
 */
export function getAncestorNodes(
  nodeId: string,
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): ReactFlowNode[] {
  const startNode = nodes.find((n) => n.id === nodeId);
  if (!startNode) {
    return [];
  }

  const ancestors: ReactFlowNode[] = [];
  const visited = new Set<string>([nodeId]);
  const queue: ReactFlowNode[] = [startNode];

  while (queue.length > 0) {
    const current = queue.shift()!;

    // Edge-based ancestors (incomers)
    const parents = getIncomers(current, nodes, edges);
    for (const parent of parents) {
      if (!visited.has(parent.id)) {
        visited.add(parent.id);
        ancestors.push(parent);
        queue.push(parent);
      }
    }

    // Subgraph parent (via parentId)
    if (current.parentId) {
      const subgraphParent = nodes.find((n) => n.id === current.parentId);
      if (subgraphParent && !visited.has(subgraphParent.id)) {
        visited.add(subgraphParent.id);
        ancestors.push(subgraphParent);
        queue.push(subgraphParent);
      }
    }
  }

  return ancestors;
}

/**
 * Gets all descendant nodes of a given node (nodes that are downstream in the graph).
 *
 * Traverses both:
 * - Edge-based descendants (nodes this node has edges pointing to)
 * - Subgraph children (nodes with parentId === this node's id) - if this node
 *   is a SUBGRAPH_PARENT, all nodes inside its subgraph are included
 *
 * @param nodeId - The ID of the node to find descendants for
 * @param nodes - All nodes in the graph
 * @param edges - All edges in the graph
 * @returns Array of descendant nodes in BFS order (closest descendants first), excludes the starting node
 */
export function getDescendantNodes(
  nodeId: string,
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): ReactFlowNode[] {
  const startNode = nodes.find((n) => n.id === nodeId);
  if (!startNode) {
    return [];
  }

  const descendants: ReactFlowNode[] = [];
  const visited = new Set<string>([nodeId]);
  const queue: ReactFlowNode[] = [startNode];

  while (queue.length > 0) {
    const current = queue.shift()!;

    // Edge-based descendants (outgoers)
    const children = getOutgoers(current, nodes, edges);
    for (const child of children) {
      if (!visited.has(child.id)) {
        visited.add(child.id);
        descendants.push(child);
        queue.push(child);
      }
    }

    // Subgraph children (nodes with parentId === current.id)
    const subgraphChildren = nodes.filter((n) => n.parentId === current.id);
    for (const child of subgraphChildren) {
      if (!visited.has(child.id)) {
        visited.add(child.id);
        descendants.push(child);
        queue.push(child);
      }
    }
  }

  return descendants;
}

/**
 * Finds the root node of a subgraph (the node with no incoming edges from other subgraph nodes).
 *
 * @param parentId - The ID of the SUBGRAPH_PARENT node, or undefined for the root graph
 * @param nodes - All nodes in the graph
 * @param edges - All edges in the graph
 * @returns The root node, or null if the subgraph is empty
 */
export function getSubgraphRoot(
  parentId: string | undefined,
  nodes: ReactFlowNode[],
  edges: ReactFlowEdge[]
): ReactFlowNode | null {
  for (const node of nodes) {
    if (node.parentId !== parentId) continue;

    // Check if this node has any incoming edge from another node in the same subgraph
    const hasIncomingEdge = edges.some(
      (e) =>
        e.target === node.id &&
        nodes.find((n) => n.id === e.source)?.parentId === parentId
    );

    if (!hasIncomingEdge) return node;
  }

  return null;
}

/**
 * Reports whether a node has at least one outgoing edge (i.e. at least one
 * child in the workflow tree).
 *
 * @param nodeId - The ID of the node to check
 * @param edges - All edges in the graph
 */
export function nodeHasChild(nodeId: string, edges: ReactFlowEdge[]): boolean {
  return edges.some((e) => e.source === nodeId);
}

/**
 * Compares two LBNodeData objects for equality.
 * Uses JSON serialization for deep comparison.
 *
 * @param a - First LBNodeData object
 * @param b - Second LBNodeData object
 * @returns true if the objects are equal, false otherwise
 */
export function isLBNodeDataEqual(
  a: LBNodeData | undefined,
  b: LBNodeData | undefined
): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

export interface IsValidChildTypeArgs {
  parentType: NodeType;
  childType: NodeType;
  /** True if the parent node is inside a subgraph (has parentId set) */
  isInsideSubgraph: boolean;
  /** True if a SUBGRAPH_PARENT node already exists anywhere in the graph */
  graphHasSubgraph: boolean;
}

/**
 * Result of checking whether a node type can be added as a child.
 * - `'valid'` — the node type is allowed and shown in the picker.
 * - `'invalidAndHidden'` — the node type is not allowed and silently
 *   omitted from the picker (e.g. triggers, deprecated node types).
 * - `{ invalidWithReason }` — the node type is shown in the picker but
 *   disabled, with a tooltip explaining why (e.g. "Only one loop per workflow").
 */
export type ChildTypeValidity =
  'valid' | 'invalidAndHidden' | { invalidWithReason: string };

/** Check if a child type is valid for a given parent, and why. */
export function isValidChildType({
  parentType,
  childType,
  isInsideSubgraph,
  graphHasSubgraph,
}: IsValidChildTypeArgs): ChildTypeValidity {
  const childInfo = getNodeInfoForType(childType);

  if (childInfo.category == NodeCategory.TRIGGER) {
    // Trigger nodes can never be children
    return 'invalidAndHidden';
  }

  if (childType === 'deprecated_filtered_shipments_loop') {
    // Existing workflows still load via the registry, but users can't create new ones.
    // Use FilterControlFlow instead.
    return 'invalidAndHidden';
  }
  if (!isProductionReadyNodeType(childType)) {
    return {
      invalidWithReason: 'This block is not available for new workflows.',
    };
  }

  const isConditional = childType === 'conditional';

  if (parentType === 'paths' && !isConditional) {
    // Paths nodes can only have conditional children
    return 'invalidAndHidden';
  }

  if (parentType !== 'paths' && isConditional) {
    // Conditional nodes can only be children of Paths
    return 'invalidAndHidden';
  }

  if (childInfo.outputType === OutputType.SUBGRAPH_PARENT) {
    if (isInsideSubgraph) {
      return { invalidWithReason: 'Loops cannot be nested' };
    }
    if (graphHasSubgraph) {
      return { invalidWithReason: 'Only one loop per workflow' };
    }
  }

  return 'valid';
}

/** Resolves to K, failing to compile if any listed key is missing from T. */
type KeysOf<T, K extends keyof T> = K;

/** Top-level LBNodeData fields that are not part of the proto `data` oneof. */
type LBNodeDataNonOneofKey = KeysOf<
  LBNodeData,
  'user_variables' | 'custom_display_name'
>;

/** The proto `data` oneof keys. The 2-arg defaultNodeData accepts only these. */
type LBNodeDataOneofKey = Exclude<keyof LBNodeData, LBNodeDataNonOneofKey>;

/**
 * Builds LBNodeData with all required top-level defaults filled in.
 *
 * Three forms:
 *  - defaultNodeData() — minimal valid LBNodeData with no oneof assigned.
 *    Use in tests that exercise behavior unrelated to node content.
 *  - defaultNodeData(nodeType) — looks up the registry and returns the
 *    NodeType's default node data.
 *  - defaultNodeData(key, data) — builds an LBNodeData literal from the
 *    given oneof key and its content, with additional LBNodeData members
 *    given reasonable defaults.
 *    Use this in NodeInfo.createDefaultNodeData implementations and in
 *    test fixtures.
 *
 * When new required top-level fields are added to LBNodeData, set their
 * defaults here so callers don't have to.
 */
export function defaultNodeData(): LBNodeDataWithIndexer;
export function defaultNodeData(nodeType: NodeType): LBNodeDataWithIndexer;
export function defaultNodeData<K extends LBNodeDataOneofKey>(
  key: K,
  data: NonNullable<LBNodeData[K]>
): LBNodeDataWithIndexer;
export function defaultNodeData(
  keyOrType?: NodeType,
  data?: unknown
): LBNodeDataWithIndexer {
  if (keyOrType === undefined) {
    return { user_variables: [] };
  }
  if (data === undefined) {
    return getNodeInfoForType(keyOrType).createDefaultNodeData();
  }
  return { user_variables: [], [keyOrType]: data };
}

/** Create a new node with the given data and a placeholder position. */
export function createNode(data: LBNodeDataWithIndexer): ReactFlowNode {
  const nodeType = getLBNodeType(data);
  return {
    // Prepend nodeType to the ID to make it slightly more human-readable
    id: `${nodeType}:${uuidv4()}`,
    type: 'lbNode',
    position: { x: 0, y: 0 },
    // Shallow freeze to catch in-place mutation of node.data.
    data: Object.freeze(data) as LBNodeDataWithIndexer,
  };
}

/**
 * Updates edgeOrder on edges from a parent to its siblings.
 * Edge order determines left-to-right ordering in calculateGraphLayout().
 *
 * @param parentId - The parent node ID
 * @param siblingIds - Ordered list of sibling node IDs (left to right)
 * @param edges - All edges in the graph
 * @returns Updated edges with reassigned edgeOrder values
 */
function updateSiblingEdgeOrders(
  parentId: string,
  siblingIds: string[],
  edges: ReactFlowEdge[]
): ReactFlowEdge[] {
  const siblingIdSet = new Set(siblingIds);
  return edges.map((edge) => {
    if (edge.source !== parentId || !siblingIdSet.has(edge.target)) {
      return edge;
    }
    const index = siblingIds.indexOf(edge.target);
    // Freeze to catch in-place mutation of edge.data.
    return {
      ...edge,
      data: Object.freeze({ ...edge.data, edgeOrder: index }),
    };
  });
}

/**
 * Finds the next available subgraphIndex for a new SUBGRAPH_PARENT node.
 * Returns max existing index + 1, or 1 if no subgraphs exist.
 */
function getNextSubgraphIndex(allNodes: ReactFlowNode[]): number {
  let maxIndex = 0;
  for (const node of allNodes) {
    const nodeInfo = getLBNodeInfo(node.data as LBNodeData);
    if (
      nodeInfo?.outputType === OutputType.SUBGRAPH_PARENT &&
      nodeInfo.getSubgraphIndex
    ) {
      const index = nodeInfo.getSubgraphIndex(node.data as LBNodeData);
      if (index !== undefined && index > maxIndex) {
        maxIndex = index;
      }
    }
  }
  return maxIndex + 1;
}

/**
 * Result of inserting a new child node into the graph.
 */
export interface InsertChildResult {
  /** The newly created child node */
  childNode: ReactFlowNode;
  /** Complete updated nodes array */
  nodes: ReactFlowNode[];
  /** Complete updated edges array */
  edges: ReactFlowEdge[];
}

/**
 * Finds the node that a new child should connect to when inserted.
 *
 * For SUBGRAPH_PARENT: returns the subgraph's root node (if any children exist)
 * For SINGLE: returns the existing child (if one exists)
 * For MULTIPLE: returns null (new children are added as siblings, not inserted before)
 *
 * @returns The insertion target node and the edge to remove (for SINGLE), or null
 */
function findInsertionTarget(
  parentNode: ReactFlowNode,
  outputType: OutputType,
  allNodes: ReactFlowNode[],
  allEdges: ReactFlowEdge[]
): { target: ReactFlowNode; edgeToRemove: ReactFlowEdge | null } | null {
  if (outputType === OutputType.SUBGRAPH_PARENT) {
    // Find subgraph root: a child with no incoming edges from other subgraph children
    const subgraphChildren = allNodes.filter(
      (n) => n.parentId === parentNode.id
    );
    if (subgraphChildren.length === 0) return null;

    const subgraphNodeIds = new Set(subgraphChildren.map((n) => n.id));
    const nodesWithIncomingEdges = new Set(
      allEdges
        .filter(
          (e) => subgraphNodeIds.has(e.source) && subgraphNodeIds.has(e.target)
        )
        .map((e) => e.target)
    );

    const subgraphRoot = subgraphChildren.find(
      (n) => !nodesWithIncomingEdges.has(n.id)
    );
    if (!subgraphRoot) {
      throw new Error(
        `Subgraph "${parentNode.id}" has children but no root node (all nodes have incoming edges)`
      );
    }

    return { target: subgraphRoot, edgeToRemove: null };
  }

  if (outputType === OutputType.SINGLE) {
    const existingChildEdge = allEdges.find((e) => e.source === parentNode.id);
    if (!existingChildEdge) return null;

    const existingChild = allNodes.find(
      (n) => n.id === existingChildEdge.target
    );
    if (!existingChild) return null;

    return { target: existingChild, edgeToRemove: existingChildEdge };
  }

  // MULTIPLE: no insertion target, children are added as siblings
  return null;
}

/**
 * Creates a new child node and connects it to the parent.
 *
 * If the parent has OutputType.SINGLE and already has a child, the new child
 * is inserted between the parent and existing child.
 *
 * If the parent has OutputType.MULTIPLE, the new child is added as the
 * rightmost sibling (highest edgeOrder).
 *
 * If the parent has OutputType.SUBGRAPH_PARENT, the new child is added inside
 * the subgraph (with parentId set). If the subgraph already has nodes, the new
 * child becomes the new root, connecting to the previous root.
 *
 * @param parentNode - The parent node to add a child to
 * @param childType - The type of child node to create
 * @param allNodes - All nodes in the graph
 * @param allEdges - All edges in the graph
 * @returns InsertChildResult with the new child node and updated nodes/edges arrays
 */
export function insertNewChildRaw(
  parentNode: ReactFlowNode,
  childType: NodeType,
  allNodes: ReactFlowNode[],
  allEdges: ReactFlowEdge[]
): InsertChildResult | null {
  const parentInfo = getLBNodeInfo(parentNode.data as LBNodeData);
  if (!parentInfo) {
    console.error(
      'insertNewChildRaw: could not get node info for parent',
      parentNode
    );
    return null;
  }

  const outputType = parentInfo.outputType ?? OutputType.SINGLE;
  const isSubgraph = outputType === OutputType.SUBGRAPH_PARENT;

  // Build the child's data payload fully — including subgraphIndex for
  // SUBGRAPH_PARENT nodes — BEFORE handing it to createNode, so the child
  // is constructed with its final data in a single shot.
  const childData = defaultNodeData(childType);
  const childInfo = getLBNodeInfo(childData);
  if (
    childInfo?.outputType === OutputType.SUBGRAPH_PARENT &&
    childInfo.setSubgraphIndex
  ) {
    childInfo.setSubgraphIndex(childData, getNextSubgraphIndex(allNodes));
  }

  const childNode = createNode(childData);

  // Set parentId for subgraph containment:
  // - If parent is a SUBGRAPH_PARENT, child goes inside it
  // - If parent is already inside a subgraph, child stays in the same subgraph
  // (Note that "parent" has an unfortunate overloaded meaning here:
  // ReactFlow uses node.parentId to the node that owns its subgraph.
  // But we're also using parent here in the more traditional "the node that
  // has a directed edge pointing to this one" sense.)
  if (isSubgraph) {
    childNode.parentId = parentNode.id;
  } else if (parentNode.parentId) {
    childNode.parentId = parentNode.parentId;
  }

  // Find the insertion target (node that new child will connect to)
  const insertion = findInsertionTarget(
    parentNode,
    outputType,
    allNodes,
    allEdges
  );

  // Build edges to add and remove
  const edgesToAdd: ReactFlowEdge[] = [];
  const edgeIdsToRemove: string[] = [];

  // Edge from parent to new child (except for SUBGRAPH_PARENT)
  if (!isSubgraph) {
    edgesToAdd.push({
      id: `edge-${uuidv4()}`,
      source: parentNode.id,
      target: childNode.id,
      type: 'lbEdge',
      markerEnd: LBEDGE_MARKER_END,
    });
  }

  // Edge from new child to insertion target (if exists)
  if (insertion) {
    edgesToAdd.push({
      id: `edge-${uuidv4()}`,
      source: childNode.id,
      target: insertion.target.id,
      type: 'lbEdge',
      markerEnd: LBEDGE_MARKER_END,
    });

    // For SINGLE, remove the old parent→target edge
    if (insertion.edgeToRemove) {
      edgeIdsToRemove.push(insertion.edgeToRemove.id);
    }
  }

  // Apply edge changes
  let updatedEdges = allEdges
    .filter((e) => !edgeIdsToRemove.includes(e.id))
    .concat(edgesToAdd);

  // Handle MULTIPLE edge ordering
  if (outputType === OutputType.MULTIPLE) {
    const existingChildEdges = allEdges.filter(
      (e) => e.source === parentNode.id
    );

    if (existingChildEdges.length > 0) {
      // Sort existing edges by edgeOrder to preserve left-to-right ordering
      const sortedExistingEdges = [...existingChildEdges].sort(
        (a, b) => (a.data?.edgeOrder ?? 0) - (b.data?.edgeOrder ?? 0)
      );

      // New child becomes rightmost sibling
      const siblingIds = [
        ...sortedExistingEdges.map((e) => e.target),
        childNode.id,
      ];

      updatedEdges = updateSiblingEdgeOrders(
        parentNode.id,
        siblingIds,
        updatedEdges
      );
    }
  }

  return {
    childNode,
    nodes: [...allNodes, childNode],
    edges: updatedEdges,
  };
}

/**
 * Creates a new child node and connects it to the parent.
 *
 * Wraps insertNewChildRaw with automatic creation of default children
 * for node types that require them (e.g. paths nodes get two conditionals).
 */
export function insertNewChild(
  parentNode: ReactFlowNode,
  childType: NodeType,
  allNodes: ReactFlowNode[],
  allEdges: ReactFlowEdge[],
  options?: {
    /** Defaults true (manual editing's existing behavior, unchanged): a
     * brand-new paths node comes with 2 ready-to-edit branches. The AI
     * Builder (agent/applyGraphOps.ts) passes false -- its ops model adds
     * every branch explicitly via a separate add_branch op, so leaving
     * this on double-counts (2 defaults + N explicit add_branch calls). */
    autoPopulatePathsChildren?: boolean;
  }
): InsertChildResult | null {
  const autoPopulatePathsChildren = options?.autoPopulatePathsChildren ?? true;
  if (childType === 'paths') {
    const existingPathsEdge = allEdges.find((edge) => {
      if (edge.source !== parentNode.id) return false;
      const target = allNodes.find((node) => node.id === edge.target);
      return target && getLBNodeType(target.data as LBNodeData) === 'paths';
    });

    if (existingPathsEdge) {
      const existingPathsNode = allNodes.find(
        (node) => node.id === existingPathsEdge.target
      );
      if (!existingPathsNode) {
        return null;
      }
      const branch = insertNewChildRaw(
        existingPathsNode,
        'conditional',
        allNodes,
        allEdges
      );
      return branch ? { ...branch, childNode: existingPathsNode } : null;
    }
  }

  let result = insertNewChildRaw(parentNode, childType, allNodes, allEdges);
  if (!result) {
    return null;
  }

  // Special 'paths' handling: auto-create two conditional children
  if (childType === 'paths' && autoPopulatePathsChildren) {
    // If paths was inserted between parent and an existing node,
    // there'll be an edge paths → existingNode. Remove it — we'll
    // re-parent the existing node under the first conditional instead.
    const pathsNodeId = result.childNode.id;
    const displacedEdge = result.edges.find((e) => e.source === pathsNodeId);

    if (displacedEdge) {
      result = {
        ...result,
        edges: result.edges.filter((e) => e.id !== displacedEdge.id),
      };
    }

    // Create two conditional children under the new paths node
    for (let i = 0; i < 2; i++) {
      const next = insertNewChildRaw(
        result.childNode,
        'conditional',
        result.nodes,
        result.edges
      );
      if (!next) {
        continue;
      }

      // Re-parent the displaced node to the first conditional
      if (i === 0 && displacedEdge) {
        next.edges.push({
          id: `edge-${uuidv4()}`,
          source: next.childNode.id,
          target: displacedEdge.target,
          type: 'lbEdge',
          markerEnd: LBEDGE_MARKER_END,
        });
      }

      result = { ...next, childNode: result.childNode };
    }
  }

  return result;
}

/**
 * Result of deleting nodes from the graph.
 */
export interface DeleteNodesResult {
  /** Updated nodes array with specified nodes removed */
  nodes: ReactFlowNode[];
  /** Updated edges array with edges connected to deleted nodes removed */
  edges: ReactFlowEdge[];
}

/**
 * Deletes the specified nodes from the graph and removes their connected edges.
 * Also deletes all descendants of the specified nodes.
 * If a deleted node's parent has OutputType.MULTIPLE, edge orders are updated.
 *
 * @param nodeIdsToDelete - IDs of nodes to delete
 * @param allNodes - All nodes in the graph
 * @param allEdges - All edges in the graph
 * @returns DeleteNodesResult with updated nodes and edges arrays
 */
export function deleteNodes(
  nodeIdsToDelete: string[],
  allNodes: ReactFlowNode[],
  allEdges: ReactFlowEdge[]
): DeleteNodesResult {
  // If no nodes to delete, return unchanged arrays
  if (nodeIdsToDelete.length === 0) {
    return { nodes: allNodes, edges: allEdges };
  }

  // Collect all nodes to delete (including all descendants - both edge-based and subgraph-based)
  const nodesToDelete = new Set<string>();
  for (const nodeId of nodeIdsToDelete) {
    nodesToDelete.add(nodeId);
    const descendants = getDescendantNodes(nodeId, allNodes, allEdges);
    for (const desc of descendants) {
      nodesToDelete.add(desc.id);
    }
  }

  // 2. Find parent nodes that need edge order updates
  //    (parents with OutputType.MULTIPLE that have remaining children)
  const parentsToUpdate = new Map<string, string[]>(); // parentId -> remaining sibling IDs

  for (const nodeId of nodeIdsToDelete) {
    const incomingEdge = allEdges.find((e) => e.target === nodeId);
    if (!incomingEdge) continue;

    const parentId = incomingEdge.source;
    const parentNode = allNodes.find((n) => n.id === parentId);
    if (!parentNode || nodesToDelete.has(parentId)) continue;

    const parentInfo = getLBNodeInfo(parentNode.data as LBNodeData);
    if (!parentInfo || parentInfo.outputType !== OutputType.MULTIPLE) continue;

    // Get all children of this parent, excluding those being deleted
    // Sort by edgeOrder to preserve left-to-right ordering
    const childEdges = allEdges
      .filter((e) => e.source === parentId && !nodesToDelete.has(e.target))
      .sort(
        (a, b) =>
          ((a.data?.edgeOrder as number) ?? 0) -
          ((b.data?.edgeOrder as number) ?? 0)
      );

    if (childEdges.length > 0) {
      parentsToUpdate.set(
        parentId,
        childEdges.map((e) => e.target)
      );
    }
  }

  // 3. Remove nodes and edges
  const updatedNodes = allNodes.filter((n) => !nodesToDelete.has(n.id));
  let updatedEdges = allEdges.filter(
    (e) => !nodesToDelete.has(e.source) && !nodesToDelete.has(e.target)
  );

  // 4. Update edge orders for each affected parent
  parentsToUpdate.forEach((siblingIds, parentId) => {
    updatedEdges = updateSiblingEdgeOrders(parentId, siblingIds, updatedEdges);
  });

  return { nodes: updatedNodes, edges: updatedEdges };
}

/** Result of reconciling a Transfer To node's escalation agent child. */
export interface ReconcileEscalationResult {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
  /** Id of a freshly materialized AI Agent node, so the caller can open it. */
  insertedChildId: string | null;
  /** Whether nodes/edges changed structurally (i.e. layout should re-run). */
  structuralChange: boolean;
}

/** The AI Agent node materialized beneath a Transfer To node's agent fallback. */
export function findTransferEscalationChild(
  transferNodeId: string,
  allNodes: ReactFlowNode[],
  allEdges: ReactFlowEdge[]
): ReactFlowNode | undefined {
  const nodeById = new Map(allNodes.map((n) => [n.id, n]));
  for (const edge of allEdges) {
    if (edge.source !== transferNodeId) continue;
    const child = nodeById.get(edge.target);
    const data = child?.data as LBNodeData | undefined;
    if (data?.ai_call_action?.is_transfer_escalation) {
      return child;
    }
  }
  return undefined;
}

/**
 * Whether a node is the read-only AI Agent mirror materialized beneath a
 * Transfer To node's "Connect to an agent" fallback. It is coupled to that
 * transfer node — it can't be deleted independently, only as part of
 * deleting the transfer node (which cascades to its descendants).
 */
export function isTransferEscalationMirror(node: ReactFlowNode): boolean {
  const data = node.data as LBNodeData | undefined;
  return data?.ai_call_action?.is_transfer_escalation === true;
}

/**
 * Data for the read-only AI Agent node mirrored beneath a Transfer To node.
 * The transfer node's own `agent_id` is the source of truth; this node is a
 * display copy. `is_transfer_escalation` makes the backend self-skip it at
 * runtime (it is delivered mid-call via register-call, not by graph traversal).
 */
function transferEscalationChildData(agentId: string): LBNodeDataWithIndexer {
  return defaultNodeData('ai_call_action', {
    channel_type: AIChannelType.CALL,
    agent_type: AIAgentType.CUSTOMIZED,
    agent_id: agentId,
    contact_id: '',
    pca_variable_names: [],
    is_inbound_agent: true,
    collect_shipment_id: false,
    is_transfer_escalation: true,
    reply_wait_seconds: 0,
    dont_wait: false,
  });
}

/**
 * Keeps a read-only AI Agent node in sync with a Transfer To node's "Connect
 * to an agent" fallback. Call after committing the transfer node's own data.
 *
 * - `CONNECT_TO_AGENT` selected: materializes the mirror node beneath the
 *   transfer node (if missing) and keeps its `agent_id` in sync with the
 *   transfer node's selection.
 * - Escalation disabled / a different option, with a mirror that is a leaf →
 *   removes it. A mirror that already has its own descendants (e.g. a
 *   downstream outbound AI action node) is left in place so switching options
 *   never destroys the user's downstream work.
 */
export function reconcileTransferEscalationChild(
  transferNodeId: string,
  allNodes: ReactFlowNode[],
  allEdges: ReactFlowEdge[]
): ReconcileEscalationResult {
  const unchanged: ReconcileEscalationResult = {
    nodes: allNodes,
    edges: allEdges,
    insertedChildId: null,
    structuralChange: false,
  };

  const transferNode = allNodes.find((n) => n.id === transferNodeId);
  const transferData = transferNode?.data as LBNodeData | undefined;
  const transfer = transferData?.transfer_to_action;
  if (!transferNode || !transfer) {
    return unchanged;
  }

  const wantsAgent =
    transfer.enable_escalation &&
    transfer.escalation_option === TransferToEscalationOption.CONNECT_TO_AGENT;
  const agentId = transfer.agent_id ?? '';
  const derivedChild = findTransferEscalationChild(
    transferNodeId,
    allNodes,
    allEdges
  );

  if (wantsAgent) {
    if (!derivedChild) {
      const result = insertNewChildRaw(
        transferNode,
        'ai_call_action',
        allNodes,
        allEdges
      );
      if (!result) {
        return unchanged;
      }
      const nodes = result.nodes.map((n) =>
        n.id === result.childNode.id
          ? { ...n, data: transferEscalationChildData(agentId) }
          : n
      );
      return {
        nodes,
        edges: result.edges,
        insertedChildId: result.childNode.id,
        structuralChange: true,
      };
    }
    // Mirror already exists — keep its agent in sync with the transfer node.
    const childData = derivedChild.data as LBNodeData;
    if (childData.ai_call_action?.agent_id !== agentId) {
      const nodes = allNodes.map((n) =>
        n.id === derivedChild.id
          ? { ...n, data: transferEscalationChildData(agentId) }
          : n
      );
      return { ...unchanged, nodes };
    }
    return unchanged;
  }

  if (derivedChild) {
    const hasOwnChildren = allEdges.some((e) => e.source === derivedChild.id);
    if (!hasOwnChildren) {
      const result = deleteNodes([derivedChild.id], allNodes, allEdges);
      return { ...result, insertedChildId: null, structuralChange: true };
    }
  }

  return unchanged;
}
